class BbdMarketSuggestionModal extends HTMLElement {
  #dialog = null;
  #config = null;
  #previousScrollY = 0;

  connectedCallback() {
    try {
      const configEl = document.getElementById('MarketSuggestionModalConfig');
      if (!configEl) return;

      this.#config = JSON.parse(configEl.textContent);
      this.#dialog = this.querySelector('dialog');

      this.#interceptLocalizationForms();
      this.#init();
    } catch (_) {
      // Fail silently
    }
  }

  async #init() {
    try {
      // Automation/testing bypass (e.g. endtest).
      // - UA token: set suite-wide in endtest's Custom User Agent (append " BbdEndtest"
      //   to endtest's own default UA so it stays a valid browser). Travels cross-origin.
      // - Cookie: readable synchronously on first load, settable per domain (single-origin).
      // - Query param: fallback for one-off manual checks.
      // All three are read synchronously, so the modal/auto-redirect never fires under test.
      const params = new URLSearchParams(window.location.search);
      const uaBypass = /BbdEndtest/i.test(navigator.userAgent);
      const cookieBypass = /(?:^|;\s*)bbdNoMarketModal=1(?:;|$)/.test(document.cookie);
      if (uaBypass || params.get('bbdNoMarketModal') === '1' || cookieBypass) return;

      if (window.Shopify?.designMode || window.Shopify?.visualPreviewMode) return;

      if (sessionStorage.getItem('marketSuggestionAutoRedirected') === '1') return;
      if (sessionStorage.getItem('marketSuggestionModalDismissed') === '1') return;

      const { currentCountry, currentLanguage } = this.#config;
      const root = window.Shopify?.routes?.root || '/';

      const url = new URL(root + 'browsing_context_suggestions.json', window.location.origin);
      url.searchParams.set('country[enabled]', 'true');
      url.searchParams.set('language[enabled]', 'true');
      if (currentCountry) url.searchParams.set('country[exclude]', currentCountry);
      if (currentLanguage) url.searchParams.set('language[exclude]', currentLanguage);

      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!res.ok) return;

      const data = await res.json();
      const suggestion = data.suggestions?.[0];
      if (!suggestion) return;

      const suggestedCountry = suggestion.parts?.country?.handle;
      const suggestedCountryName = suggestion.parts?.country?.name;
      const suggestedLanguage = suggestion.parts?.language?.handle;

      // Only show for country-level differences; ignore language-only
      if (!suggestedCountry || suggestedCountry.toUpperCase() === currentCountry.toUpperCase()) return;

      const uppercasedCountry = suggestedCountry.toUpperCase();

      const englishCountryName = (() => {
        try {
          return new Intl.DisplayNames(['en'], { type: 'region' }).of(uppercasedCountry) || suggestedCountryName;
        } catch (_) {
          return suggestedCountryName;
        }
      })();

      const manualMarket = localStorage.getItem('marketSuggestionManualMarket');
      if (this.#config.autoRedirect && !manualMarket) {
        this.#redirect(uppercasedCountry, suggestedLanguage);
      } else {
        this.#populate(uppercasedCountry, englishCountryName, suggestedLanguage);
        this.#openDialog();
      }
    } catch (_) {
      // Fail silently
    }
  }

  #interceptLocalizationForms() {
    const modalForm = this.querySelector('.bbd-market-suggestion-modal__form');
    document.querySelectorAll('form').forEach((form) => {
      if (form === modalForm) return;
      if (!form.action?.includes('/localization')) return;

      const nativeSubmit = HTMLFormElement.prototype.submit.bind(form);
      form.submit = () => {
        const countryCode = form.querySelector('[name="country_code"]')?.value;
        if (countryCode) {
          try {
            localStorage.setItem('marketSuggestionManualMarket', countryCode);
            localStorage.removeItem('marketSuggestionAutoRedirect');
          } catch (_) {
            // Fail silently
          }
        }
        nativeSubmit();
      };
    });
  }

  #redirect(suggestedCountry, suggestedLanguage) {
    try {
      sessionStorage.setItem('marketSuggestionAutoRedirected', '1');
      localStorage.setItem('marketSuggestionAutoRedirect', '1');
    } catch (_) {
      // Fail silently
    }
    const countryInput = this.querySelector('.bbd-market-suggestion-modal__country-input');
    const languageInput = this.querySelector('.bbd-market-suggestion-modal__language-input');
    const returnToInput = this.querySelector('[name="return_to"]');
    if (countryInput) countryInput.value = suggestedCountry;
    if (languageInput) languageInput.value = suggestedLanguage || '';
    if (returnToInput) returnToInput.value = window.location.pathname + window.location.search;
    this.querySelector('.bbd-market-suggestion-modal__form')?.submit();
  }

  #populate(suggestedCountry, suggestedCountryName, suggestedLanguage) {
    const { heading, body, acceptLabel, cancelLabel } = this.#config;

    const titleEl = this.querySelector('.bbd-market-suggestion-modal__title');
    const bodyEl = this.querySelector('.bbd-market-suggestion-modal__body');
    const acceptBtn = this.querySelector('[data-accept]');
    const cancelBtn = this.querySelector('[data-cancel]');
    const countryInput = this.querySelector('.bbd-market-suggestion-modal__country-input');
    const languageInput = this.querySelector('.bbd-market-suggestion-modal__language-input');

    if (titleEl) titleEl.textContent = heading;
    if (bodyEl) bodyEl.textContent = body.replace('{country}', suggestedCountryName || suggestedCountry);
    if (acceptBtn) acceptBtn.textContent = acceptLabel;
    if (cancelBtn) cancelBtn.textContent = cancelLabel;
    if (countryInput) countryInput.value = suggestedCountry;
    if (languageInput) languageInput.value = suggestedLanguage || '';

    cancelBtn?.addEventListener('click', () => this.#cancel());
    acceptBtn?.addEventListener('click', () => this.#accept());

    this.#dialog?.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#cancel();
    });

    // Native Escape fires a 'cancel' event — intercept to set sessionStorage
    this.#dialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#cancel();
    });
  }

  #openDialog() {
    if (!this.#dialog || this.#dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.documentElement.setAttribute('scroll-lock', '');
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      this.#dialog.showModal();
      this.querySelector('[data-accept]')?.focus();
    });
  }

  #cancel() {
    try {
      sessionStorage.setItem('marketSuggestionModalDismissed', '1');
    } catch (_) {
      // Fail silently
    }
    this.#closeDialog();
  }

  #accept() {
    try {
      localStorage.setItem('marketSuggestionAutoRedirect', '1');
      localStorage.removeItem('marketSuggestionManualMarket');
    } catch (_) {
      // Fail silently
    }
    const returnToInput = this.querySelector('[name="return_to"]');
    if (returnToInput) returnToInput.value = window.location.pathname + window.location.search;
    this.querySelector('.bbd-market-suggestion-modal__form')?.submit();
  }

  async #closeDialog() {
    if (!this.#dialog?.open) return;

    this.#dialog.classList.add('dialog-closing');

    await new Promise((resolve) => {
      this.#dialog.addEventListener('animationend', resolve, { once: true });
      setTimeout(resolve, 300);
    });

    const otherOpenDialogs = Array.from(document.querySelectorAll('dialog[open]')).filter((d) => d !== this.#dialog);
    const activeLockers = Array.from(document.querySelectorAll('[scroll-lock]:not(html)')).filter((el) => {
      if (el.tagName === 'DIALOG') return false;
      if (el.tagName === 'DETAILS') return el.open;
      return el.offsetWidth > 0 && el.offsetHeight > 0;
    });

    if (otherOpenDialogs.length === 0 && activeLockers.length === 0) {
      document.documentElement.removeAttribute('scroll-lock');
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    this.#dialog.close();
    this.#dialog.classList.remove('dialog-closing');
  }
}

if (!customElements.get('bbd-market-suggestion-modal')) {
  customElements.define('bbd-market-suggestion-modal', BbdMarketSuggestionModal);
}
